import React, { useState, useEffect } from "react";
import api from "../../globals/axios";
import { styled } from "@mui/material/styles";
import { Close } from "@mui/icons-material";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAlert } from "../Alert";

import SoilTestScoreCard from "../../screens/farmer/SoilTestScoreCard";
import AMSatelliteDataViewer from "../SatelliteCharts/AMSatelliteDataViewer";
import FarmSummaryCard from "./FarmSummaryCard";
import LocateLand from "../LocateLand";
import { LoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../../globals/const";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  " .MuiDialog-container": {
    width: "100%",
    margin: "0px",
  },
  " .MuiDialog-paper": {
    width: "100%",
    maxWidth: "100%",
    maxHeight: "100%",
  },
  " .MuiPaper-root": {
    margin: "0px",
  },
}));

const FarmDetailsPopup = ({ farmId, handlePopupClose }) => {
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [farmDetails, setFarmDetails] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const fetchFarmDetails = async (id) => {
    try {
      const response = await api.get(`/app-admin/get-farm-full-details/${id}`);
      if (response?.data?.data) {
        setFarmDetails(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching farm details", error);
      showAlert("Error fetching farm details", "error");
    }
  };

  useEffect(() => {
    fetchFarmDetails(farmId);

    return () => {
      setFarmDetails(null);
    };
  }, []);

  return (
    <>
      <BootstrapDialog
        onClose={handlePopupClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2, fontWeight: "700" }}
          id="customized-dialog-title"
        >
          {t("Farm Details")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePopupClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent dividers fullWidth>
          {farmDetails && (
            <>
              <FarmSummaryCard
                farmSummary={{
                  [t("Farm Id")]: farmDetails.farm_data?.id || "N/A",
                  [t("Farm Name")]: farmDetails.farm_data?.farm_name || "N/A",
                  [t("Area")]: farmDetails.farm_data?.area || "N/A",
                  [t("Is Approved")]: farmDetails.farm_data?.is_approved
                    ? t("Yes")
                    : t("No"),
                  [t("State")]: farmDetails.farm_data?.address?.state || "N/A",
                  [t("District")]:
                    farmDetails.farm_data?.address?.district || "N/A",
                  [t("Pincode")]:
                    farmDetails.farm_data?.address?.pin_code || "N/A",
                  [t("Crops Planted")]:
                    farmDetails.farm_data?.crops?.reduce(
                      (names, crop) =>
                        `${crop?.commodity?.name || ""},${names}`,
                      ""
                    ) || "N/A",
                }}
              />
              <Card
                sx={{
                  padding: 2,
                  marginTop: 2,
                  marginBottom: 2,
                }}
                fullWidth
              >
                <CardContent fullWidth>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="h6">
                        {t("Farm Health Scores")}
                      </Typography>
                      <SoilTestScoreCard
                        scores={farmDetails.scores}
                        showHealthScore
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Typography variant="h6">{t("Locate Land")}</Typography>
                      <LoadScript
                        googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                        onLoad={() => setIsScriptLoaded(true)}
                      >
                        <LocateLand
                          path={farmDetails.farm_data?.polygon || []}
                          center={farmDetails.farm_data?.polygon[0] || []}
                          editable={false}
                          setPath={() => {}}
                          setCenter={() => {}}
                          setCurrentLocation={() => {}}
                          isScriptLoaded={isScriptLoaded}
                        />
                      </LoadScript>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              <Card fullWidth>
                <CardContent fullWidth>
                  <Typography variant="h6">{t("Satellite Data")}</Typography>
                  <AMSatelliteDataViewer
                    farmId={farmId}
                    polygon={farmDetails.farm_data?.polygon || []}
                  />
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
      </BootstrapDialog>
    </>
  );
};

export default FarmDetailsPopup;
